import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { Image } from 'expo-image'
import { Text } from 'react-native'
import { Theme } from '@theme'
import { useTheme } from '@react-navigation/native'
import { formatNoteDate, getCommonStyles, splitNoteHashtags } from '@utils'
import { Icon } from '../atoms'
import { Note, useGetContactById } from '@hooks'
import { useCurrentUserStore } from '@store'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

export const NoteItem = ({
  note,
  onPress,
  isSelected,
}: {
  note: Note
  onPress: (noteId: string) => void
  isSelected?: boolean
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { userID } = useCurrentUserStore()

  const otherAccountId = note.createdByAccountId === userID
    ? note.contactAccountId
    : note.isShared
      ? note.createdByAccountId
      : null
  const { data: contact } = useGetContactById(otherAccountId || '')

  // Date and time on every note ("Aujourd'hui · 14:48", "12 août"), a two-line
  // preview, and the hashtags on their own line.
  const dateLabel = formatNoteDate(note.createdAt, {
    today: t('notes.dates.today'),
    yesterday: t('notes.dates.yesterday'),
  })
  const { text, hashtags } = splitNoteHashtags(note.content)

  // Where the note comes from, when it was not typed by hand.
  const sourceBadge =
    note.source === 'assistant' || note.source === 'voice' ? (
      <View style={styles.badge}>
        <Icon
          name={note.source === 'assistant' ? 'star' : 'microphone'}
          size={theme.iconSize.extraSmall}
          color={theme.colors.primary}
        />
        <Text style={styles.badgeText}>{t(`notes.source.${note.source}`)}</Text>
      </View>
    ) : null

  return (
    <TouchableOpacity key={note.id} style={styles.noteItem} onPress={() => onPress(note.id)}>
      <View style={styles.noteContainer}>
        <View style={styles.noteContent}>
          {contact && (
            <View style={[commonStyles.row, styles.contactRow]}>
              <Image
                source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
                style={styles.contactAvatar}
                cachePolicy="memory-disk"
                contentFit="cover"
              />
              <Text style={styles.contactName} numberOfLines={1} ellipsizeMode="tail">
                {contact.fullName}
              </Text>
              {sourceBadge}
            </View>
          )}
          {!contact && sourceBadge && <View style={styles.badgeRow}>{sourceBadge}</View>}
          {!!note.title && (
            <View style={[commonStyles.row, styles.titleRow]}>
              {note.isPinned && (
                <Icon name="pin" size={theme.iconSize.extraSmall} color={theme.colors.primary} />
              )}
              <Text style={styles.noteTitle}>{note.title}</Text>
            </View>
          )}
          <Text style={styles.noteTime}>{dateLabel}</Text>
          {!!text && (
            <Text style={styles.noteText} numberOfLines={2} ellipsizeMode="tail">
              {text}
            </Text>
          )}
          {hashtags.length > 0 && (
            <Text style={styles.noteHashtags} numberOfLines={1}>
              {hashtags.join(' ')}
            </Text>
          )}
        </View>
        <Icon name="arrow" color={isSelected ? theme.colors.primary : theme.colors.lightGrey} />
      </View>
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    noteItem: {
      paddingHorizontal: theme.spacing.sm,
      paddingBottom: theme.spacing.lg,
      marginTop: theme.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey20,
    },
    noteContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    noteContent: {
      flex: 1,
    },
    contactRow: {
      alignItems: 'center',
      marginBottom: theme.spacing.xs,
    },
    contactAvatar: {
      width: 24,
      height: 24,
      borderRadius: 12,
      marginRight: theme.spacing.sm,
    },
    contactName: {
      flex: 1,
      fontSize: theme.fontSize.medium,
      fontWeight: '600',
      color: theme.colors.text,
    },
    titleRow: {
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    noteTitle: {
      fontSize: theme.fontSize.medium,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 2,
    },
    badgeRow: {
      flexDirection: 'row',
      marginBottom: theme.spacing.xs,
    },
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      marginLeft: theme.spacing.sm,
    },
    badgeText: {
      fontSize: theme.fontSize.extraSmall,
      color: theme.colors.greyBlue,
    },
    noteTime: {
      fontSize: theme.fontSize.small,
      color: theme.colors.lightGrey,
      marginBottom: 2,
    },
    noteText: {
      fontSize: theme.fontSize.small,
      color: theme.colors.lightGrey,
      lineHeight: 18,
    },
    noteHashtags: {
      fontSize: theme.fontSize.small,
      color: theme.colors.primary,
      marginTop: 2,
    },
  })
